import { NgModule } from '@angular/core';
import { Routes, RouterModule, ROUTES, UrlSegment, UrlMatchResult } from '@angular/router';
import { StandComponent } from './stand.component';
import { StandService } from 'app/stand/stand.service';
import { StandHeaderComponent } from './components/header/stand-header.component';
import { UserRolesEnum } from '@collections/users/enums/user-roles.enums';
import { UsersService } from '@shared/services/users/users.service';
import { StandFooterComponent } from './components/footer/stand-footer.component';
import { InfoBarMobileComponent } from './components/info-bar/info-bar-mobile/info-bar-mobile.component';
import { StandLayoutEnum } from './stand.enums';
import { PageNotFoundError } from 'app/error-handler/custom-errors/page-not-found.error';

function routesFactory(usersService: UsersService): Routes {
  return [
    {
      path: '',
      component: StandFooterComponent,
      outlet: 'footerOutlet',
      data: {
        shouldReuseOnSlide: false
      },
    },
    {
      path: '',
      component: InfoBarMobileComponent,
      outlet: 'mobileinfoBarOutlet',
      data: {
        shouldReuseOnSlide: false
      },
    },
  {
    path: ':companySlug',
    component: StandComponent,
    data: {
      breadcrumb: 'subhall',
      shouldReuseOnSlide: false
    },
    resolve: {
      resolvedData: StandService
    },
    children: [
      {
        path: '',
        component: StandHeaderComponent,
        outlet: 'topOutlet',
        data: {
          shouldReuseOnSlide: false
        },
      },
      {
        path: '',
        pathMatch: 'full',
        loadChildren: () => import('./pages/dashboard/dashboard.module').then(m => m.DashboardModule),
        data: {
          isCacheable: true,
          breadcrumb: $localize`Online Stand`,
          layout: StandLayoutEnum.complete
        }
      },
      {
        path: 'request-info-company/:requestType',
        loadComponent: () => import('./pages/request-info-company/request-info-company.component').then(c => c.RequestInfoCompanyComponent),
        data: {
          roles: [UserRolesEnum.basic],
          isCacheable: true,
          layout: StandLayoutEnum.complete
        },
        canLoad: ['RoleGuard']
      },
      {
        path: 'news',
        loadChildren: () => import('./pages/news/news.module').then(m => m.NewsModule),
        data: {
          isCacheable: true,
          breadcrumb: $localize`News`,
          layout: StandLayoutEnum.complete
        }
      },
      {
        path: 'news/:slug',
        loadChildren: () => import('./pages/news-details/news-details.module').then(m => m.NewsDetailsModule),
        data: {
          isCacheable: true,
          layout: StandLayoutEnum.detailsPage
        }
      },
      {
        path: 'products/:slug',
        loadChildren: () => import('./pages/product-details/product-details.module').then(m => m.ProductDetailsModule),
        data: {
          isCacheable: true,
          layout: StandLayoutEnum.detailsPage
        },
      },
      // https://github.com/angular/angular/issues/12088#issuecomment-907775873
      // attualmente non si può caricare condizionalmente un modulo utilizzando la stessa path
      // per cui la soluzione migliore al momento è quella suggerita nell'issue su github.
      // Utilizzando una custom factory (useFactory) sulle routes del modulo, è possibile iniettare l'usersService,
      //  e con un custom matcher nella route è possibile caricare condizionalmente un modulo diverso per gli utenti autorizzati.
      // Non è l'utilizzo canonico, ad esempio non include un RoleGuard, e bisogna fare attenzione all'ordine delle routes,
      // (la route products/:slug deve stare sopra, altrimenti non viene presa in considerazione), ma per il resto sembra funzionare.
      {
        loadChildren: () => import('../stand-administration/stand-products-manager/stand-products-manager.module').then(m => m.StandProductsManagerModule),
        data: {
          isCacheable: true,
          breadcrumb: $localize`Products`,
          roles: [UserRolesEnum.admin, UserRolesEnum.stand_admin],
          layout: StandLayoutEnum.complete
        },
        matcher: (url: UrlSegment[]): UrlMatchResult | null => {
          if (!url[0].path.endsWith('products')) {
            return null;
          }
          if (url.length > 1) { // ammette solo url ./products
            throw new PageNotFoundError(url.map(segment => segment.path).join('/'));
          }
          if (!usersService.isInRole([UserRolesEnum.admin, UserRolesEnum.stand_admin])) {
            return null;
          }
          return { consumed: url };
        },
        // canLoad: ['RoleGuard']
      },
      {
        loadChildren: () => import('./pages/products/products.module').then(m => m.ProductsModule),
        data: {
          isCacheable: true,
          breadcrumb: $localize`Products`,
          layout: StandLayoutEnum.complete
        },
        matcher: (url: UrlSegment[]): UrlMatchResult | null => {
          if (!url[0].path.endsWith('products')) {
            return null;
          }
          
          // (corregge url search console: /hall/machines/machines-for-electric-cables-and-optical-fibers/stand/gloser-srl-marketing-and-technology/products/pay-off-and-take-up-machines/1000/pay-off-and-take-up-machines/tubular-strander-for-cables)
          // da capire da dove arriva questo url, se da qualche parte nell'aplicazione.
          if (url.length > 1) { // ammette solo url ./products 
            throw new PageNotFoundError(url.map(segment => segment.path).join('/'));
          }
          if (usersService.isInRole([UserRolesEnum.admin, UserRolesEnum.stand_admin])) {
            return null;
          }
          return { consumed: url };
        },
      },
      // {
      //   path: 'contacts',
      //   loadChildren: () => import('./pages/contacts/contacts.module').then(m => m.ContactsModule),
      //   data: {
      //     isCacheable: true,
      //     breadcrumb: $localize`Contacts`
      //   }
      // },
    ]
  }
];
}
@NgModule({
    providers: [
        {
            provide: ROUTES,
            useFactory: routesFactory,
            multi: true,
            deps: [UsersService]
        },
    ],
    exports: [RouterModule]
})
export class StandRoutingModule {
}
